import {useRef, useState, useEffect} from "react";
import {useTranslation} from "react-i18next";
import i18n from "../locales/i18n";
import styles from "./LanguageSelector.module.scss";
import langaugeIconRiize from "../assets/images/riize/language_icon_riize.png";
import langaugeIconAespa from "../assets/images/aespa/language_icon_aespa.png";
import {ARTIST, Artist} from "../utils/artist.utils";

const SUPPORTED_LANGS = ["ko", "en"] as const;
type SupportedLng = (typeof SUPPORTED_LANGS)[number];

const getLngAbbr = (lng: SupportedLng) => {
  if (lng === "ko") return "KO";
  if (lng === "en") return "EN";
  return "KO";
};

const getCurrentLng = (): SupportedLng => {
  const raw = i18n.language;
  if (raw.startsWith("ko")) return "ko";
  if (raw.startsWith("en")) return "en";
  return "ko";
};

const LanguageSelector = ({artist}: {artist: Artist}) => {
  const {t} = useTranslation("common");
  const [isOpen, setIsOpen] = useState(false);
  const [currentLng, setCurrentLng] = useState<SupportedLng>(getCurrentLng);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleLanguageChanged = () => setCurrentLng(getCurrentLng());
    i18n.on("languageChanged", handleLanguageChanged);
    return () => i18n.off("languageChanged", handleLanguageChanged);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleToggle = () => setIsOpen((prev) => !prev);

  const handleSelect = (lng: SupportedLng) => {
    i18n.changeLanguage(lng);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className={styles.container}>
      <div className={styles.iconWrapper}>
        <img
          src={artist === ARTIST.aespa ? langaugeIconAespa : langaugeIconRiize}
          className={styles.icon}
          alt=""
        />{" "}
      </div>
      <button
        type="button"
        className={`${styles.trigger} ${artist}`}
        onClick={handleToggle}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={t(`language.${currentLng}`)}
      >
        <span></span>
        <span className={styles.label}>{getLngAbbr(currentLng)}</span>
        <span className={`${styles.chevron} ${artist}`}>
          <ChevronDownIcon />
        </span>
      </button>
      {isOpen && (
        <ul
          className={`${styles.dropdown} ${artist}`}
          role="listbox"
          aria-activedescendant={`lang-${currentLng}`}
        >
          {SUPPORTED_LANGS.map((lng) => (
            <li
              key={lng}
              role="option"
              id={`lang-${lng}`}
              aria-selected={currentLng === lng}
            >
              <button
                type="button"
                className={`${styles.option} ${artist}`}
                onClick={() => handleSelect(lng)}
              >
                {getLngAbbr(lng)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LanguageSelector;

const ChevronDownIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M6 9L12 15L18 9"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
